import { aesGcmOpen, aesGcmSeal } from '@/crypto/aes-gcm'
import { base64ToBytes, bytesToBase64 } from '@/crypto/encoding'
import { randomIvBytes } from '@/crypto/random'

/**
 * Track F: Emergency Access cryptographic primitives (KEY_HIERARCHY.md §4.7.5)。
 *
 * 鍵階層 (grantee 側):
 *   - emergencyPubKey / emergencyPrivKey  — RSA-OAEP-2048 / SHA-256 / MGF1-SHA-256
 *   - emergencyPubKey  は SPKI DER (base64) で公開保管
 *   - emergencyPrivKey は PKCS#8 DER 形式で export → SK で AES-256-GCM wrap → base64 保管
 *     server は復号できない (zero-knowledge)
 *
 * 鍵階層 (granter 側):
 *   - SK_granter (32 byte) を grantee の emergencyPubKey で RSA-OAEP wrap → wrappedSk
 *     server は wrappedSk を opaque blob として保管、grantee の privKey でのみ復号可能
 *
 * 制約:
 *   - RSA-OAEP-2048 の最大 plaintext は ~190 byte。SK = 32 byte なので余裕。
 *   - keygen は ~300-800ms (デバイス性能依存) → init-keypair UI で spinner 必須。
 */

const RSA_OAEP_PARAMS = {
  name: 'RSA-OAEP',
  modulusLength: 2048,
  publicExponent: new Uint8Array([0x01, 0x00, 0x01]),
  hash: 'SHA-256',
} as const

const RSA_USAGES: KeyUsage[] = ['encrypt', 'decrypt', 'wrapKey', 'unwrapKey']

/**
 * Web Crypto subtle accessor (browser / node の差を吸収)。
 */
const subtle = (): SubtleCrypto => {
  if (typeof globalThis.crypto === 'undefined' || globalThis.crypto.subtle === undefined) {
    throw new Error('Web Crypto API not available')
  }

  return globalThis.crypto.subtle
}

export interface ExportedKeypair {
  /** SPKI DER (公開鍵、~294 byte) */
  pubKeySpki: Uint8Array
  /** PKCS#8 DER (秘密鍵、~1.2 KiB) */
  privKeyPkcs8: Uint8Array
}

/**
 * RSA-OAEP-2048 keypair を生成 + export する。
 * private key は extractable で生成して PKCS#8 を取り出す (後で SK で wrap)。
 */
export const generateEmergencyKeypair = async (): Promise<ExportedKeypair> => {
  const keypair = await subtle().generateKey(
    RSA_OAEP_PARAMS,
    true, // extractable = true (export して PKCS#8 を取り出す)
    RSA_USAGES,
  )

  const [pubKeySpkiBuf, privKeyPkcs8Buf] = await Promise.all([
    subtle().exportKey('spki', keypair.publicKey),
    subtle().exportKey('pkcs8', keypair.privateKey),
  ])

  return {
    pubKeySpki: new Uint8Array(pubKeySpkiBuf),
    privKeyPkcs8: new Uint8Array(privKeyPkcs8Buf),
  }
}

export interface WrappedEmergencyPrivKey {
  /** base64(ciphertext || authTag) */
  wrappedPrivKey: string
  /** base64(iv 12 byte) */
  iv: string
}

/**
 * 自分の SK で PKCS#8 形式の private key を AES-256-GCM 暗号化する。
 * 結果は server に保管 (`wrappedEmergencyPrivKey` + `wrappedEmergencyPrivKeyIv`)。
 *
 * caller は呼び出し後すぐに `privKeyPkcs8.fill(0)` で平文を zeroize すべき。
 */
export const wrapEmergencyPrivKey = async (
  privKeyPkcs8: Uint8Array,
  sk: Uint8Array,
): Promise<WrappedEmergencyPrivKey> => {
  const iv = randomIvBytes()
  const sealed = await aesGcmSeal(sk, iv, privKeyPkcs8)

  return {
    wrappedPrivKey: bytesToBase64(sealed),
    iv: bytesToBase64(iv),
  }
}

/**
 * wrappedPrivKey + iv (base64) を SK で復号して RSA private key (CryptoKey) を取り出す。
 *
 * 戻り値は CryptoKey なので raw byte は exposure されない。caller は unwrapSkForGranter で
 * 使った後に参照を切ること (V8 GC まかせ)。
 */
export const unwrapEmergencyPrivKey = async (
  wrappedPrivKeyB64: string,
  ivB64: string,
  sk: Uint8Array,
): Promise<CryptoKey> => {
  const iv = base64ToBytes(ivB64)
  const sealed = base64ToBytes(wrappedPrivKeyB64)
  const pkcs8 = await aesGcmOpen(sk, iv, sealed)

  try {
    return await subtle().importKey(
      'pkcs8',
      pkcs8 as Uint8Array<ArrayBuffer>,
      RSA_OAEP_PARAMS,
      false,
      ['decrypt', 'unwrapKey'],
    )
  } finally {
    pkcs8.fill(0)
  }
}

/**
 * Granter side: grantee の emergencyPubKey (SPKI base64) で SK_granter を RSA-OAEP wrap。
 * 戻り値の base64 は server に PATCH /grants/:id/wrap で送信する。
 */
export const wrapSkForGrantee = async (
  granteePubKeySpkiB64: string,
  skGranter: Uint8Array,
): Promise<string> => {
  const spki = base64ToBytes(granteePubKeySpkiB64)

  const pubKey = await subtle().importKey(
    'spki',
    spki as Uint8Array<ArrayBuffer>,
    RSA_OAEP_PARAMS,
    false,
    ['encrypt'],
  )

  const wrapped = new Uint8Array(
    await subtle().encrypt({ name: 'RSA-OAEP' }, pubKey, skGranter as Uint8Array<ArrayBuffer>),
  )

  return bytesToBase64(wrapped)
}

/**
 * Grantee side: 自分の emergencyPrivKey (CryptoKey) で wrappedSk (base64) を RSA-OAEP unwrap。
 * 戻り値は SK_granter (32 byte Uint8Array)。caller は使い終わったら zeroize する。
 */
export const unwrapSkForGranter = async (
  wrappedSkB64: string,
  granteePrivKey: CryptoKey,
): Promise<Uint8Array> => {
  const wrapped = base64ToBytes(wrappedSkB64)

  const sk = new Uint8Array(
    await subtle().decrypt(
      { name: 'RSA-OAEP' },
      granteePrivKey,
      wrapped as Uint8Array<ArrayBuffer>,
    ),
  )

  return sk
}
